import { useEffect, useState, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { getActiveAccount, getMsalInstance, getScopes, RETURN_TO_KEY } from "./msalConfig.js";

/**
 * Phase 342 — the portal's single auth boundary.
 *
 * <p>Children render only once a silent token acquisition has succeeded. Anything
 * else — no account, an expired session, a failure of any other kind — ends in a
 * sign-in redirect with the deep link stashed, so a client who lands on a bookmarked
 * report comes back to that report rather than the root.</p>
 *
 * <p>Every portal route sits inside this gate: there is no anonymous surface here at
 * all (ADR-081 D3). `/auth/callback` is the one route mounted OUTSIDE it, at the
 * App level, because it has to consume the redirect before any silent acquisition
 * can possibly succeed.</p>
 */
export function AuthGate({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<"checking" | "ready" | "redirecting">("checking");
  const location = useLocation();

  useEffect(() => {
    const msal = getMsalInstance();
    const scopes = getScopes();

    const redirectToSignIn = (reason: unknown) => {
      // The credential is never part of what we record: only the path we want back.
      sessionStorage.setItem(RETURN_TO_KEY, location.pathname + location.search);
      setStatus("redirecting");
      msal.loginRedirect({ scopes }).catch((err) => {
        // eslint-disable-next-line no-console
        console.error("client-portal: loginRedirect failed", err, reason);
      });
    };

    const account = getActiveAccount();
    if (!account) {
      redirectToSignIn("no account");
      return;
    }

    msal
      .acquireTokenSilent({ account, scopes })
      .then(() => setStatus("ready"))
      .catch((err) => {
        if (!(err instanceof InteractionRequiredAuthError)) {
          // eslint-disable-next-line no-console
          console.error("client-portal: acquireTokenSilent failed", err);
        }
        redirectToSignIn(err);
      });
    // Mount only: re-running per navigation would fire a redundant silent refresh on
    // every link click. Per-request freshness is the fetcher's job, not the gate's.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (status !== "ready") return <div className="auth-loading">Checking your session…</div>;
  return <>{children}</>;
}
